import { albumBuilderConstants as C } from "../actions/constants/albumBuilderConstants";
import { eventAlbumConstants } from "@/actions/constants/eventAlbumConstants";
import type { AlbumTemplate } from "../engine/album/contracts/template";
import type { AlbumPage } from "../engine/album/contracts/page";
import type { RenderMode } from "../engine/album/contracts/renderer";
import type { AlbumNode, ImageNode } from "../engine/album/contracts/nodes";
import type {
    MoveNodePayload,
    ResizeNodePayload,
    RotateImagePayload,
    SetNodeOpacityPayload,
    UpdateNodeStylePayload,
    UpdateCropPayload,
    ChangeAssetPayload,
    ReorderNodesPayload,
    SetBackgroundColorPayload,
    SetBackgroundAssetPayload,
} from "../engine/album/contracts/mutations";
import { PayloadAction } from "@reduxjs/toolkit";
import { failure, request, returnType, success } from "@/utils/constants";
import { EventAlbum } from "./eventAlbumReducer";
import { resizeSiblingLayout } from "@/engine/album/utils/resizeSiblingSlots";
import { materializePageVersion, getPageNodeIdsForVersion } from "@/engine/album/utils/materializePageVersion";

export type PageLoadStatus = | "idle" | "loading" | "loaded" | "error";

export interface AlbumBuilderState {
    mode: "album" | "template";
    templateList: AlbumTemplate[];
    templateListLoading: boolean;
    templateListError: string | null;

    template: AlbumTemplate | null;
    album: EventAlbum | null;
    templateLoading: boolean;
    templateError: string | null;

    pages: Record<string, AlbumPage>;
    initialPages: Record<string, AlbumPage>;
    loadingPageIds: string[];

    activePageId: string | null;
    selectedNodeId: number | null;
    renderMode: RenderMode;
    pendingPageOperation: "delete" | null;
    mutationLoading: boolean;
    mutationError: string | null;
    selectedHistoryIdx: { nodeId: number | null; index: number | null };

    saveLoading: boolean;
    saveError: string | null;
    savedAt: number | null;
    dirtySheets: string[];
    undoStack: Array<{ pageId: string; nodes: ImageNode[] }>;
    redoStack: Array<{ pageId: string; nodes: ImageNode[] }>;

    copyValue: Record<string, unknown>;

    createdTemplateId: string | null;
    visiblePageIds: string[];
    previewLoading: boolean;
    previewPagesReady: boolean;
    previewReady: boolean;
    previewError: string | null;
    pageLoadStatus: Record<string, PageLoadStatus>;
    pageLoadErrors: Record<string, string | null>;
    requestedVersion: number | null;
}

const INITIAL: AlbumBuilderState = {
    mode: "template",
    templateList: [],
    templateListLoading: false,
    templateListError: null,
    template: null,
    album: null,
    templateLoading: false,
    templateError: null,
    pages: {},
    initialPages: {},
    loadingPageIds: [],
    activePageId: null,
    selectedNodeId: null,
    renderMode: "builder",
    mutationLoading: false,
    selectedHistoryIdx: { nodeId: null, index: null },
    mutationError: null,
    saveLoading: false,
    saveError: null,
    savedAt: null,
    dirtySheets: [],
    undoStack: [],
    redoStack: [],
    copyValue: {},
    createdTemplateId: null,
    visiblePageIds: [],
    previewLoading: false,
    previewPagesReady: false,
    previewReady: false,
    previewError: null,
    pageLoadStatus: {},
    pageLoadErrors: {},
    requestedVersion: null,
    pendingPageOperation: null, 
};

function normalizeTemplate(raw: any): AlbumTemplate {
    if (!raw) return raw;
    return { ...raw, id: raw.id ?? raw._id?.toString() ?? "" };
}

function normalizePage(raw: any): AlbumPage {
    if (!raw) return raw;
    return { ...raw, id: raw.id ?? raw._id?.toString() ?? "" };
}

function updateNode<T>(
    state: AlbumBuilderState,
    pageId: string,
    nodeIndex: number,
    fn: (node: T) => T,
): AlbumBuilderState {
    const page = state.pages[pageId];
    if (!page) return state;

    const node = page.nodes[nodeIndex];
    if (!node) return state;

    const nodes = [...page.nodes];
    nodes[nodeIndex] = fn(node as T) as any;

    return {
        ...state,
        pages: {
            ...state.pages,
            [pageId]: {
                ...page,
                nodes,
            },
        },
    };
}

function updatePage(
    state: AlbumBuilderState,
    pageId: string,
    fn: (page: AlbumPage) => AlbumPage,
): AlbumBuilderState {
    const page = state.pages[pageId];
    if (!page) return state;
    return { ...state, pages: { ...state.pages, [pageId]: fn(page) } };
}

function templateListReducer(
    state: AlbumBuilderState,
    action: { type: string; payload?: any },
): AlbumBuilderState {
    switch (action.type) {
        case C.listTemplatesRequest:
            return { ...state, templateListLoading: true, templateListError: null };

        case C.listTemplatesSuccess:
            return {
                ...state,
                templateListLoading: false,
                templateList: (action.payload as any[]).map(normalizeTemplate),
            };

        case C.listTemplatesFailure:
            return { ...state, templateListLoading: false, templateListError: action.payload };

        default:
            return state;
    }
}

function templateReducer(
    state: AlbumBuilderState,
    action: { type: string; payload?: any },
): AlbumBuilderState {
    switch (action.type) {
        case C.fetchTemplateRequest:
        case C.createTemplateRequest:
        case C.updateTemplateRequest:
            return { ...state, templateLoading: true, templateError: null };

        case C.fetchTemplateSuccess:
        case C.createTemplateSuccess:
        case C.updateTemplateSuccess:
            return {
                ...state,
                templateLoading: false,
                template: normalizeTemplate(action.payload),
            };

        case C.createTemplateRedirect:
            return { ...state, createdTemplateId: action.payload };

        case C.fetchTemplateFailure:
        case C.createTemplateFailure:
        case C.updateTemplateFailure:
            return { ...state, templateLoading: false, templateError: action.payload };
        case C.setMode:
            return {
                ...state,
                mode: action.payload,
            };
        case C.setVisiblePageIds:
            return {
                ...state,
                visiblePageIds: action.payload,
            };
        default:
            return state;
    }
}

function albumReducer(
    state: AlbumBuilderState,
    action: { type: string; payload?: any },
): AlbumBuilderState {
    switch (action.type) {
        case C.fetchAlbumRequest:
            // case C.fetchAlbumPageRequest:
            return { ...state, templateLoading: true, templateError: null };
        case C.fetchAlbumSuccess:
            // case C.updateAlbumRequest:
            return { ...state, templateLoading: false, album: action.payload?.album, requestedVersion: action.payload.version ?? null };
        case C.fetchAlbumFailure:
            // case C.updateAlbumFailure:
            return { ...state, templateLoading: false, templateError: action.payload };
        default:
            return state;
    }
}

function pageReducer(
    state: AlbumBuilderState,
    action: { type: string; payload?: any },
): AlbumBuilderState {
    switch (action.type) {
        case C.setPageLoading: {
            const pageId = action.payload;
        
            return {
                ...state,
                pageLoadStatus: {
                    ...state.pageLoadStatus,
                    [pageId]: "loading",
                },
                pageLoadErrors: {
                    ...state.pageLoadErrors,
                    [pageId]: null,
                },
            };
        }
        case C.setPageLoaded: {
            const pageId = action.payload;
        
            return {
                ...state,
                pageLoadStatus: {
                    ...state.pageLoadStatus,
                    [pageId]: "loaded",
                },
                pageLoadErrors: {
                    ...state.pageLoadErrors,
                    [pageId]: null,
                },
            };
        }
        case C.setPageLoadError: {
            const { pageId, error } = action.payload;
        
            return {
                ...state,
                pageLoadStatus: {
                    ...state.pageLoadStatus,
                    [pageId]: "error",
                },
                pageLoadErrors: {
                    ...state.pageLoadErrors,
                    [pageId]: error,
                },
            };
        }
case C.fetchAlbumPagesRequest: {
    const pageIds = (action.payload?.pageIds ?? []) as string[];

    return {
        ...state,
        loadingPageIds: [
            ...new Set([
                ...state.loadingPageIds,
                ...pageIds,
            ]),
        ],
    };
}
case C.fetchPageSuccess:
case C.fetchAlbumPagesSuccess: {
    const pages = (action.payload as AlbumPage[]) ?? [];

    if (!pages.length) {
        return state;
    }

    const nextPages = {
        ...state.pages,
    };

    const nextInitialPages = {
        ...state.initialPages,
    };
    const nextPageLoadStatus = { ...state.pageLoadStatus };
    const loadedPageIds: string[] = [];

    for (const rawPage of pages) {
        const page = normalizePage(rawPage);

        if (!page?.id) {
            continue;
        }

        // const effectivePage = materializePageVersion(
        //     page,
        //     state.requestedVersion,
        // );

        // nextPages[page.id] = effectivePage;

        // nextInitialPages[page.id] =
        //     JSON.parse(
        //         JSON.stringify(effectivePage),
        //     );
const effectivePage = materializePageVersion(
    page,
    state.requestedVersion,
);
console.log("requestedVersion", state.requestedVersion);

console.log(
    "RAW page.nodes",
    page.nodes.map((n: any) => n._id)
);

console.log(
    "membershipIds",
    getPageNodeIdsForVersion(page, state.requestedVersion)
);

console.log(
    "effectivePage.nodes",
    effectivePage.nodes.map((n: any) => ({
        id: n._id,
        version: n.version,
        assetId: n.content?.assetId,
    }))
);

nextPages[page.id] = effectivePage;

nextInitialPages[page.id] =
    JSON.parse(
        JSON.stringify(page),
    );
        loadedPageIds.push(page.id);
        nextPageLoadStatus[page.id] = "loaded";
    }

    return {
        ...state,
        pages: nextPages,
        initialPages: nextInitialPages,
        pageLoadStatus: nextPageLoadStatus,
        loadingPageIds:
            state.loadingPageIds.filter(
                (id) =>
                    !loadedPageIds.includes(id),
            ),
    };
}
        // case eventAlbumConstants.fetchPagesSuccess: {
        //     const pages = (action.payload as AlbumPage[]) ?? [];
        
        //     if (!pages.length) {
        //         return state;
        //     }
        
        //     const nextPages = { ...state.pages };
        //     const nextInitialPages = { ...state.initialPages };
        //     const loadedPageIds: string[] = [];
        
        //     for (const rawPage of pages) {
        //         const page = normalizePage(rawPage);
        
        //         if (!page?.id) continue;
        
        //         nextPages[page.id] = page;
        
        //         nextInitialPages[page.id] = JSON.parse(
        //             JSON.stringify(page),
        //         );
        
        //         loadedPageIds.push(page.id);
        //     }
        
        //     return {
        //         ...state,
        //         pages: nextPages,
        //         initialPages: nextInitialPages,
        //         loadingPageIds: state.loadingPageIds.filter(
        //             (id) => !loadedPageIds.includes(id),
        //         ),
        //     };
        // }
        // case C.fetchPageSuccess:
        // case C.fetchAlbumPageSuccess:
        // case C.loadPage: {
        //     const page = normalizePage(action.payload);
        //     if (!page?.id) return state;
        //     const frozenPage = JSON.parse(JSON.stringify(page));
        //     return {
        //         ...state,
        //         loadingPageIds: state.loadingPageIds.filter((id) => id !== page.id),
        //         pages: { ...state.pages, [page.id]: page },
        //         initialPages: { ...state.initialPages, [page.id]: frozenPage },
        //     };
        // }
case C.fetchAlbumPageSuccess:
case C.loadPage: {
    const page = normalizePage(action.payload);

    if (!page?.id) {
        return state;
    }

    const effectivePage = materializePageVersion(
        page,
        state.requestedVersion,
    );

    const frozenPage = JSON.parse(
        JSON.stringify(page),
    );

    return {
        ...state,

        loadingPageIds:
            state.loadingPageIds.filter(
                (id) => id !== page.id,
            ),

        pages: {
            ...state.pages,
            [page.id]: effectivePage,
        },

        initialPages: {
            ...state.initialPages,
            [page.id]: frozenPage,
        },
    };
}
// case eventAlbumConstants.fetchPagesSuccess: {
//     const pages = (action.payload as AlbumPage[]) ?? [];

//     if (!pages.length) {
//         return state;
//     }

//     const nextPages = { ...state.pages };
//     const nextInitialPages = { ...state.initialPages };
//     const loadedPageIds: string[] = [];

//     for (const rawPage of pages) {
//         const page = normalizePage(rawPage);

//         if (!page?.id) continue;

//         nextPages[page.id] = page;

//         nextInitialPages[page.id] = JSON.parse(
//             JSON.stringify(page),
//         );

//         loadedPageIds.push(page.id);
//     }

//     return {
//         ...state,
//         pages: nextPages,
//         initialPages: nextInitialPages,
//         loadingPageIds: state.loadingPageIds.filter(
//             (id) => !loadedPageIds.includes(id),
//         ),
//     };
// }
        case C.unloadPage: {
            const { [action.payload]: _removed, ...remaining } = state.pages;
            return { ...state, pages: remaining };
        }

        case C.fetchPageFailure:
        case C.fetchAlbumPageFailure:
            return {
                ...state,
                loadingPageIds: state.loadingPageIds.filter(
                    (id) => id !== (action.payload as { pageId?: string })?.pageId,
                ),
            };

        case C.createPageSuccess:
        case C.createAlbumPageSuccess: {
            const page = normalizePage(action.payload);
            const current = state[state.mode];
            if (!current) {
                return state;
            }
            return {
                ...state,
                [state.mode]: {
                    ...current,
                    pageOrder: [...current.pageOrder, page.id],
                    pages: [
                        ...current.pages,
                        {
                            pageId: page.id,
                            pageNumber: page.pageNumber,
                            label: `Sheet ${page.pageNumber}`,
                            thumbnailAssetId: null,
                        },
                    ],
                },
            };
        }

        case C.deletePageSuccess: {
            const { [action.payload]: _del, ...rest } = state.pages;
            const current = state[state.mode];
            if (!current) {
                return state;
            }
            return {
                ...state,
                pages: rest,
                [state.mode]: {
                    ...current,
                    pageOrder: current.pageOrder.filter((id) => id !== action.payload),
                    pages: current.pages.filter((p) => p.pageId !== action.payload),
                },
            };
        }

        case C.reorderPagesSuccess:
            return {
                ...state,
                template: state.template ? { ...state.template, pageOrder: action.payload } : null,
            };

        default:
            return state;
    }
}

function previewReducer(
    state: AlbumBuilderState,
    action: { type: string; payload?: any },
): AlbumBuilderState {
    switch (action.type) {
        case C.preparePreviewRequest:
            return {
                ...state,
                previewLoading: true,
                previewPagesReady: false,
                previewReady: false,
                previewError: null,
            };

        case C.preparePreviewPagesReady:
            return {
                ...state,
                previewLoading: true,
                previewPagesReady: true,
                previewReady: false,
                previewError: null,
            };

        case C.preparePreviewReady:
            return {
                ...state,
                previewLoading: false,
                previewPagesReady: true,
                previewReady: true,
                previewError: null,
            };

        case C.preparePreviewFailure:
            return {
                ...state,
                previewLoading: false,
                previewPagesReady: false,
                previewReady: false,
                previewError:
                    action.payload ??
                    "Failed to prepare album preview",
            };

        case C.clearPreviewState:
            return {
                ...state,
                previewLoading: false,
                previewPagesReady: false,
                previewReady: false,
                previewError: null,
            };

        default:
            return state;
    }
}

function mutationReducer(
    state: AlbumBuilderState,
    action: { type: string; payload?: any },
): AlbumBuilderState {
    const LOCAL_EDIT_TYPES = new Set([
        C.moveNode,
        C.resizeNode,
        C.rotateImage,
        C.setNodeOpacity,
        C.updateNodeStyle,
        C.updateCrop,
        C.changeAsset,
        C.reorderNodes,
        C.setBackgroundColor,
        C.setBackgroundAsset,
        C.setBackgroundOpacity,
        C.addGrid,
        C.splitSlotV,
        C.splitSlotH,
        C.renameSheet,
        C.cutSlotImages,
        C.pasteSlotImages,
        C.swapSlotImages,
        C.deleteSlot,
        C.expandSlot,
        C.resizeSiblingSlots,
    ]);
    if (LOCAL_EDIT_TYPES.has(action.type as any) && action.payload?.pageId) {
        const pid = action.payload.pageId as string;
        if (!state.dirtySheets.includes(pid)) {
            state = { ...state, dirtySheets: [...state.dirtySheets, pid] };
        }
    }
    switch (action.type) {
        case C.createPageRequest:
        case C.deletePageRequest:
        case C.reorderPagesRequest:
        case C.addNodeRequest:
        case C.removeNodeRequest:
        case C.createAlbumPageRequest:
            return { ...state, mutationLoading: true, mutationError: null };

        case C.createPageSuccess:
        case C.deletePageSuccess:
        case C.reorderPagesSuccess:
            return { ...state, mutationLoading: false };

        case C.fetchPageFailure:
            return { ...state, mutationError: action.payload?.message ?? null };

        case C.createPageFailure:
        case C.deletePageFailure:
        case C.reorderPagesFailure:
        case C.addNodeFailure:
        case C.removeNodeFailure:
            return { ...state, mutationLoading: false, mutationError: action.payload };

        case C.addNodeSuccess: {
            const { pageId, node } = action.payload;
            const page = state.pages[pageId];
            if (!page) return { ...state, mutationLoading: false };
            return {
                ...state,
                mutationLoading: false,
                pages: {
                    ...state.pages,
                    [pageId]: {
                        ...page,
                        nodes: { ...page.nodes, [node.id]: node },
                    },
                },
            };
        }

        case C.removeNodeSuccess: {
            const { pageId, nodeId } = action.payload;
            const page = state.pages[pageId];
            if (!page) return { ...state, mutationLoading: false };
            const { [nodeId]: _n, ...nodesLeft } = page.nodes;
            return {
                ...state,
                mutationLoading: false,
                pages: {
                    ...state.pages,
                    [pageId]: {
                        ...page,
                        nodes: nodesLeft,
                    },
                },
            };
        }
        case C.historySelected: {
            const {nodeId,index} = action.payload;
            return {
                ...state,
                selectedHistoryIdx: {
                    nodeId,
                    index
                },
            };
        }

        case C.moveNode: {
            const { pageId, nodeId, x, y } = action.payload as MoveNodePayload;
            return updateNode(state, pageId, nodeId, (n: any) => ({
                ...n,
                transform: { ...n.transform, x, y },
            }));
        }
        case C.resizeNode: {
            const { pageId, nodeId, x, y, width, height } = action.payload as ResizeNodePayload;
            return updateNode(state, pageId, nodeId, (n: any) => ({
                ...n,
                transform: { ...n.transform, x, y, width, height },
            }));
        }
        case C.rotateImage: {
            const { pageId, nodeId, rotation } = action.payload as RotateImagePayload;
            return updateNode(state, pageId, nodeId, (n: any) => ({
                ...n,
                content: { ...n.content, 
                    crop: {
                        ...n.content.crop,
                        rotation
                    }
                },
            }));
        }
        case C.setNodeOpacity: {
            const { pageId, nodeId, opacity } = action.payload as SetNodeOpacityPayload;
            return updateNode(state, pageId, nodeId, (n: any) => ({
                ...n,
                transform: { ...n.transform, opacity },
            }));
        }
        case C.updateNodeStyle: {
            const { pageId, nodeId, style } = action.payload as UpdateNodeStylePayload;
            return updateNode(state, pageId, nodeId, (n: any) => ({
                ...n,
                style: { ...n.style, ...style },
            }));
        }
        case C.updateCrop: {
            const { pageId, nodeId, crop } = action.payload as UpdateCropPayload;
            return updateNode(state, pageId, nodeId, (n: any) => ({
                ...n,
                content: { ...n.content, crop: { ...n.content.crop, ...crop } },
            }));
        }
        case C.changeAsset: {
            const { pageId, nodeId, assetId, thumbnailAssetId } = action.payload as ChangeAssetPayload;
            return updateNode(state, pageId, nodeId, (n: any) => ({
                ...n,
                content: { ...n.content, assetId, thumbnailAssetId: thumbnailAssetId ?? n.content?.thumbnailAssetId, placeholder: false },
            }));
        }
        case C.reorderNodes: {
            const { pageId, nodeIds } = action.payload as { pageId: string; nodeIds: number[] };
            const page = state.pages[pageId];
            if (!page) return state;

            const nodes = [...(page.nodes as ImageNode[])];

            // Get image node indices in current order
            const imageIndices = nodes
                .map((n, i) => ({ n, i }))
                .filter(({ n }) => n?.type === "image")
                .map(({ i }) => i);

            // snapshot for undo
            const snapshot = { pageId, nodes: page.nodes as ImageNode[] };

            // Extract content from each node in the NEW desired order
            const reorderedContents = nodeIds.map((srcIdx) => {
                const n = nodes[srcIdx] as ImageNode;
                return n?.content ?? null;
            });

            const newNodes = [...nodes];
            imageIndices.forEach((slotIdx, position) => {
                newNodes[slotIdx] = {
                    ...(nodes[slotIdx] as ImageNode),
                    content: reorderedContents[position], // swap only content, keep transform/layout
                };
            });

            return {
                ...state,
                undoStack: [...state.undoStack.slice(-19), snapshot],
                redoStack: [],
                pages: {
                    ...state.pages,
                    [pageId]: { ...page, nodes: newNodes },
                },
                dirtySheets: state.dirtySheets.includes(pageId)
                    ? state.dirtySheets
                    : [...state.dirtySheets, pageId],
            };
        }
        case C.setBackgroundColor: {
            const { pageId, color } = action.payload as SetBackgroundColorPayload;
            return updatePage(state, pageId, (p) => ({ ...p, backgroundColor: color }));
        }
        case C.setBackgroundAsset: {
            const { pageId, assetId } = action.payload as SetBackgroundAssetPayload;
            return updatePage(state, pageId, (p) => ({ ...p, backgroundAssetId: assetId }));
        }
        case C.setBackgroundOpacity: {
            const { pageId, opacity } = action.payload as { pageId: string; opacity: number };
            return updatePage(state, pageId, (p) => ({ ...p, backgroundOpacity: opacity }));
        }

        case C.addGrid: {
            const { pageId, nodes } = action.payload as {
                pageId: string;
                nodes: ImageNode[];
            };
            const page = state.pages[pageId];
            if (!page) return state;
            const snapshot = { pageId, nodes: page.nodes };
            return {
                ...state,
                undoStack: [...state.undoStack.slice(-19), snapshot],
                redoStack: [],
                pages: { ...state.pages, [pageId]: { ...page, nodes } },
            };
        }

        case C.splitSlotV:
        case C.splitSlotH: {
            const { pageId, parentId, childA, childB } = action.payload as {
                pageId: string;
                parentId: number;
                childA: ImageNode;
                childB: ImageNode;
            };
            const page = state.pages[pageId];
            if (!page) return state;
            const snapshot = { pageId, nodes: page.nodes as ImageNode[] };

            // Update parent node and insert the two new children after it
            const newNodes = [...page.nodes] as ImageNode[];

            newNodes.splice(parentId, 1, childA, childB);

            return {
                ...state,
                undoStack: [...state.undoStack.slice(-19), snapshot],
                redoStack: [],
                pages: {
                    ...state.pages,
                    [pageId]: {
                        ...page,
                        nodes: newNodes as any,
                    },
                },
            };
        }

        case C.renameSheet: {
            const { pageId, label } = action.payload as { pageId: string; label: string };
            const page = state.pages[pageId];
            if (!page) return state;
            return { ...state, pages: { ...state.pages, [pageId]: { ...page, label } } };
        }
        case C.applyHistorySnapshot: {
          const { pageId, page, dirtySheets } = action.payload as {
            pageId: string;
            page: any;
            dirtySheets: string[];
          };
        
          return {
            ...state,
            pages: {
              ...state.pages,
              [pageId]: page,
            },
            dirtySheets,
          };
        }
        // ─── Copy / Cut / Paste / Swap ────────────────────────────────────────────

        case C.copySlotImages: {
            const { pageId, nodeId } = action.payload as { pageId: string; nodeId: number };
            const page = state.pages[pageId];
            if (!page) return state;
            const node = page.nodes[nodeId] as ImageNode | undefined;
            if (!node) return state;
            const assetId = node.content?.assetId ?? null;
            const assetThumbNail = node.content?.thumbnailAssetId ?? null;
            return {
                ...state,
                copyValue: {
                    assetId: assetId,
                    thumbnailAssetId: assetThumbNail,
                },
            };
        }

        case C.cutSlotImages: {
            const { pageId, nodeId } = action.payload as { pageId: string; nodeId: number };
            const page = state.pages[pageId];
            if (!page) return state;
            const node = page.nodes[nodeId] as ImageNode | undefined;
            if (!node) return state;
            const assetId = node.content?.assetId ?? null;
            const assetThumbNail = node.content?.thumbnailAssetId ?? null;
            return updateNode(
                {
                    ...state,
                    copyValue: {
                        assetId: assetId,
                        thumbnailAssetId: assetThumbNail,
                    },
                },
                pageId,
                nodeId,
                (n: any) => ({
                    ...n,
                    content: {
                        ...n.content,
                        assetId: null,
                        thumbnailAssetId: null,
                        placeholder: true,
                    },
                }),
            );
        }

        case C.pasteSlotImages: {
            const { pageId, nodeId } = action.payload as { pageId: string; nodeId: number };
            if (!state.copyValue.assetId && !state.copyValue.thumbnailAssetId) return state;
            return updateNode(state, pageId, nodeId, (n: any) => ({
                ...n,
                content: {
                    ...n.content,
                    assetId: state.copyValue.assetId,
                    thumbnailAssetId: state.copyValue.thumbnailAssetId,
                    placeholder: false,
                },
            }));
        }

        case C.swapSlotImages: {
            const { pageId, sourceId, targetId } = action.payload as {
                pageId: string;
                sourceId: number;
                targetId: number;
            };
            if (sourceId === targetId) return state;
            const page = state.pages[pageId];
            if (!page) return state;
            const src = page.nodes[sourceId] as ImageNode | undefined;
            const tgt = page.nodes[targetId] as ImageNode | undefined;
            if (!src || !tgt) return state;
            const srcAsset = {
                assetId: src.content?.assetId ?? null,
                thumbnailAssetId: src.content?.thumbnailAssetId ?? null,
            };
            const tgtAsset = {
                assetId: tgt.content?.assetId ?? null,
                thumbnailAssetId: tgt.content?.thumbnailAssetId ?? null,
            };
            const newNodes = [...page.nodes] as ImageNode[];
            newNodes[sourceId] = {
                ...src,
                content: {
                    ...src.content,
                    ...tgtAsset,
                    placeholder: !tgtAsset,
                },
            };
            newNodes[targetId] = {
                ...tgt,
                content: {
                    ...tgt.content,
                    ...srcAsset,
                    placeholder: !srcAsset,
                },
            };
            return {
                ...state,
                pages: {
                    ...state.pages,
                    [pageId]: {
                        ...page,
                        nodes: newNodes as any,
                    },
                },
            };
        }

        case C.deleteSlot: {
            const { pageId, nodeId } = action.payload as { pageId: string; nodeId: number };
            const page = state.pages[pageId];
            if (!page) return state;

            const nodes = page.nodes as ImageNode[];
            const target = nodes[nodeId];
            if (!target) return state;

            const alive = nodes.filter(Boolean).length;
            
            if (alive === 1) {
            
              if (state.mode === "template") {
                    const pageOrder: any = state?.template?.pageOrder;
            
                    if (pageOrder.length <= 1) {
                        return state;
                    }
            
                    const pageIndex = pageOrder.indexOf(pageId);
            
                    if (pageIndex === -1) {
                        return state;
                    }
            
                    const nextPageId =
                        pageOrder[pageIndex + 1] ??
                        pageOrder[pageIndex - 1] ??
                        null;
            
                    const nextPages = { ...state.pages };
                    delete nextPages[pageId];
            
                    const nextPageOrder = pageOrder.filter(
                        (id:any) => id !== pageId,
                    );
            
                    return {
                        ...state,
                        undoStack: [
                            ...state.undoStack.slice(-19),
                            {
                                type: "delete-page",
                                pageId,
                                nodes,
                                pageOrder,
                            },
                        ],
                        redoStack: [],
                        selectedNodeId: null,
                        pages: nextPages,
                        pageOrder: nextPageOrder,
                        activePageId: state.activePageId === pageId ? nextPageId : state.activePageId,
                    };
                }
            
                const pageOrder:any = state?.album?.pageOrder;
            
                if (pageOrder.length <= 1) {
                    return state;
                }
            
                const pageIndex = pageOrder.indexOf(pageId);
            
                if (pageIndex === -1) {
                    return state;
                }
            
                const nextPageId =
                    pageOrder[pageIndex + 1] ??
                    pageOrder[pageIndex - 1] ??
                    null;
            
                const nextPages = { ...state.pages };
                delete nextPages[pageId];
            
                const nextPageOrder = pageOrder.filter(
                    (id:any) => id !== pageId,
                );
            
                return {
                    ...state,
                    undoStack: [
                        ...state.undoStack.slice(-19),
                        {
                            pageId,
                            nodes,
                        },
                    ],
                    redoStack: [],
                    selectedNodeId: null,
                    pages: nextPages,
                    activePageId:
                        state.activePageId === pageId
                            ? nextPageId
                            : state.activePageId,
                };
            }

            const snapshot = { pageId, nodes };
            const GAP = 12;
            const near = (a: number, b: number) => Math.abs(a - b) <= 1.5;
            const t = target.transform;

            let newNodes = [...nodes] as (ImageNode | null)[];

            // 1) Look for a single neighbor whose rect, unioned with the deleted slot's
            //    rect, forms one clean rectangle. If found, it grows to take over.
            let siblingId: number | null = null;
            for (let i = 0; i < nodes.length; i++) {
                if (i === nodeId || !nodes[i]) continue;
                const s = nodes[i].transform;
                const sameRow = near(s.y, t.y) && near(s.height, t.height);
                const sameCol = near(s.x, t.x) && near(s.width, t.width);
                const leftOf = near(s.x + s.width + GAP, t.x);
                const rightOf = near(t.x + t.width + GAP, s.x);
                const above = near(s.y + s.height + GAP, t.y);
                const below = near(t.y + t.height + GAP, s.y);
                if ((sameRow && (leftOf || rightOf)) || (sameCol && (above || below))) {
                    siblingId = i;
                    break;
                }
            }

            if (siblingId !== null) {
                const s = newNodes[siblingId]!.transform;
                const x = Math.min(s.x, t.x);
                const y = Math.min(s.y, t.y);
                const right = Math.max(s.x + s.width, t.x + t.width);
                const bottom = Math.max(s.y + s.height, t.y + t.height);

                newNodes[siblingId] = {
                    ...newNodes[siblingId]!,
                    transform: { ...s, x, y, width: right - x, height: bottom - y },
                };
                newNodes[nodeId] = null;
            } else {
                newNodes[nodeId] = null;

                const rowPeers = newNodes
                    .map((n, i) => ({ n, i }))
                    .filter(
                        ({ n }) =>
                            n && near(n.transform.y, t.y) && near(n.transform.height, t.height),
                    ) as { n: ImageNode; i: number }[];
                const colPeers = newNodes
                    .map((n, i) => ({ n, i }))
                    .filter(
                        ({ n }) =>
                            n && near(n.transform.x, t.x) && near(n.transform.width, t.width),
                    ) as { n: ImageNode; i: number }[];

                const redistribute = (peers: { n: ImageNode; i: number }[], axis: "x" | "y") => {
                    const sorted = [...peers].sort((a, b) =>
                        axis === "x"
                            ? a.n.transform.x - b.n.transform.x
                            : a.n.transform.y - b.n.transform.y,
                    );
                    const N = sorted.length;
                    const hiKey = axis === "x" ? "width" : "height";
                    const lo = Math.min(t[axis], sorted[0].n.transform[axis]);
                    const hi = Math.max(
                        t[axis] + t[hiKey],
                        sorted[N - 1].n.transform[axis] + sorted[N - 1].n.transform[hiKey],
                    );
                    const span = hi - lo;
                    const newSize = Math.floor((span - GAP * (N - 1)) / N);

                    sorted.forEach(({ i }, k) => {
                        const pos = lo + k * (newSize + GAP);
                        newNodes[i] = {
                            ...newNodes[i]!,
                            transform: {
                                ...newNodes[i]!.transform,
                                [axis]: pos,
                                [hiKey]: newSize,
                            },
                        };
                    });
                };

                if (rowPeers.length > 1) redistribute(rowPeers, "x");
                else if (colPeers.length > 1) redistribute(colPeers, "y");
            }



            const resultNodes = newNodes.filter((n): n is ImageNode => !!n);
            
            const newSelectedId = state.selectedNodeId === nodeId ? null : state.selectedNodeId;

return {
    ...state,
    undoStack: [
        ...state.undoStack.slice(-19),
        snapshot,
    ],
    pendingPageOperation: "delete",
    redoStack: [],
    selectedNodeId: newSelectedId,
    pages: {
        ...state.pages,
        [pageId]: {
            ...page,
            nodes: resultNodes,
        },
    },
};
        }

case C.resizeSiblingSlots: {
    const {
        pageId,
        nodeId,
        dx,
        dy,
        initialTransforms,
    } = action.payload;

    const page = state.pages[pageId];

    if (!page) {
        return state;
    }

    const result =
        resizeSiblingLayout(
            nodeId,
            dx,
            dy,
            initialTransforms,
        );

    const nodes =
        page.nodes as ImageNode[];

    const newNodes = nodes.map(
        (node, i) => ({
            ...node,
            transform: {
                ...node.transform,
                ...result.transforms[i],
            },
        }),
    );

    return {
        ...state,
        pages: {
            ...state.pages,
            [pageId]: {
                ...page,
                nodes: newNodes,
            },
        },
    };
}
        case C.saveRequest:
            return { ...state, saveLoading: true, saveError: null };
        case C.saveSuccess: {
            const cleanId = action.payload as string;
            const savedPage = state.pages[cleanId];
            return {
                ...state,
                saveLoading: false,
                savedAt: Date.now(),
                dirtySheets: state.dirtySheets.filter((id) => id !== cleanId),
                initialPages: savedPage
                    ? {
                          ...state.initialPages,
                          [cleanId]: JSON.parse(JSON.stringify(savedPage)),
                      }
                    : state.initialPages,
            };
        }
        case C.saveFailure:
            return { ...state, saveLoading: false, saveError: action.payload };

        case C.saveTemplateRequest:
        case C.saveAlbumRequest:
            return { ...state, saveLoading: true, saveError: null };
        case C.saveTemplateSuccess:
            return { ...state, saveLoading: false, savedAt: Date.now(), dirtySheets: [] };
        case C.saveTemplateFailure:
            return { ...state, saveLoading: false, saveError: action.payload };

        case C.applyPages: {
            const pages = action.payload as AlbumPage[];
            const ids = pages.map((page) => page.id).filter((id): id is string => !!id);
            const nextPages = { ...state.pages };
            for (const page of pages) {
                const pageId = page.id;
                if (!pageId) continue;
                nextPages[pageId] = { ...normalizePage(page), id: pageId } as AlbumPage;
            }
            const nextDirty = [...state.dirtySheets];
            for (const id of ids) {
                if (!nextDirty.includes(id)) nextDirty.push(id);
            }
            return { ...state, pages: nextPages, dirtySheets: nextDirty };
        }

        case C.markDirty: {
            const id = action.payload as string;
            if (state.dirtySheets.includes(id)) return state;
            return { ...state, dirtySheets: [...state.dirtySheets, id] };
        }
        case C.markClean: {
            const id = action.payload as string;
            return { ...state, dirtySheets: state.dirtySheets.filter((d) => d !== id) };
        }

        default:
            return state;
    }
}

function builderUiReducer(
    state: AlbumBuilderState,
    action: { type: string; payload?: any },
): AlbumBuilderState {
    switch (action.type) {
        case C.setActivePage:
            return { ...state, activePageId: action.payload, selectedNodeId: null };

        case C.setSelectedNode:
            return { ...state, selectedNodeId: action.payload };

        case C.setRenderMode:
            return { ...state, renderMode: action.payload };

        case C.createPageSuccess: {
            const page = action.payload;
            const pageId = page.id ?? page._id?.toString();
            return { ...state, activePageId: pageId };
        }

        case C.deletePageSuccess:
            return {
                ...state,
                activePageId: state.activePageId === action.payload ? null : state.activePageId,
                selectedNodeId: null,
            };

        default:
            return state;
    }
}

export function albumBuilderReducer(
    state: AlbumBuilderState = INITIAL,
    action: { type: string; payload?: any },
): AlbumBuilderState {
    if (action.type === C.reset) return INITIAL;

    let next = state;
    next = templateListReducer(next, action);
    next = templateReducer(next, action);
    next = albumReducer(next, action);
    next = pageReducer(next, action);
    next = previewReducer(next,action);
    next = mutationReducer(next, action);
    next = builderUiReducer(next, action);

    return next;
}
interface BackgroundState extends returnType {
    background: any;
    backgroundList: [];
    backgroundListed: boolean;
}

export const createBackgroundSas = (
    state: BackgroundState = {
        isFailure: false,
        isLoading: false,
        isSuccess: false,
        background: {},
        backgroundList: [],
        backgroundListed: false,
    },
    action: PayloadAction,
) => {
    switch (action.type) {
        case C.createBackgroundSasRequest:
        case C.getAlumBackgroundRequest:
            return {
                ...state,
                ...request,
            };
        case C.createBackgroundSasSuccess:
            return {
                ...state,
                ...success,
                background: action.payload,
            };
        case C.getAlumBackgroundSuccess:
            return {
                ...state,
                backgroundListed: true,
                backgroundList: action.payload,
                isLoading: false,
            };

        case C.createBackgroundSasFailure:
        case C.getAlumBackgroundFailure:
            return {
                ...state,
                ...failure,
                backgroundListed: false,
            };
        default:
            return state;
    }
};
